/**
 * vehicles.ts — the vehicle MODEL registry (M35 Stage 2). Maps each racer to a
 * 3D vehicle (.glb in public/models/, hand-built in Blender) so the field is a
 * mix of distinct vehicles, not one repeated shape. The CarView loads the model,
 * fits it to the physics box, and recolours the "Paint" part per racer — so
 * gameplay (the fixed collision box + all of tuning.ts) is untouched.
 *
 * Add a vehicle: drop its .glb in public/models/, add a VehicleDef, and list its
 * id in ROSTER. `vehicleForRacer(id)` cycles the roster by racer slot.
 */
export interface VehicleDef {
  id: string;
  /** Path under public/ (vite serves it at BASE_URL). */
  url: string;
  /** World-unit length to scale the model to (~4 = the car footprint). */
  length: number;
  /** True if the model already faces +Z; false flips it 180° (Blender exports
   *  its +Y front as glTF -Z, so Blender-built cars use false). */
  forwardIsZ: boolean;
  /** M93 (owner rule): the vehicle's DEFAULT stock livery — what an undressed
   *  car wears EVERYWHERE (garage, lobby, races) until skins are equipped.
   *  Car paint no longer follows the racer's ball colour: the BALL alone is
   *  your on-course identity (slot 0 = red, slot 1 = blue, … — fixed order,
   *  offline and online). The ui/vehicleCatalog ACCENTS derive from these. */
  stockColor: number;
  /** M151: the stock RIM finish an undressed ride wears (the approved pack:
   *  dark gunmetal on the red rides and the blue sports car, polished chrome
   *  on the black muscle car). applyCarMaterials reads it whenever no tire
   *  set is equipped; absent = the classic bright alloy. */
  rimColor?: number;
  /** M151: a mirror-chrome rim finish (the muscle car's deep-dish five-spokes). */
  rimChrome?: boolean;
  /** M154: the stock colour BEHIND the spokes (the barrel), when the pack
   *  paints it (the monster truck's red wheel centres). Absent = dark trim.
   *  A tire set or a kit's wheel colours still choose their own barrel. */
  barrelColor?: number;
}

const base = import.meta.env.BASE_URL;

export const VEHICLES: Record<string, VehicleDef> = {
  // M151 (owner's approved concept pack, 2026-10-13): every ride is rebuilt to
  // its DESIGN_BRIEF and wears the pack's colour — one deep racing red
  // (0xe0202a) on the coupe, pickup, monster truck, motorbike and Formula,
  // electric blue on the sports car, black on the muscle convertible. The
  // helmets (= Paint, the M50 rule) follow the vehicle colour automatically.
  // The ui/vehicleCatalog ACCENTS derive from these (with a steel override
  // for the black muscle car, whose own colour would vanish on the menus).
  // M62: the STARTER Coupe — a small 2-door every player begins with (the other
  // five unlock by lifetime points). A missing coupe.glb falls back to the
  // procedural body in CarView, so this ships before the model is built.
  // M152 (owner: "size the vehicles appropriately compared to each other"):
  // the fleet is scaled as a LADDER of real-world lengths: coupe 3.8 <
  // sports 4.1 < muscle 4.5 < monster truck 4.6 (M153: the pickup 4.9 → 4.65,
  // the owner's "slightly smaller"; M154: 4.65 → 4.4 with its bumpers tucked
  // in to the concept pack's ends, so on paper it now sits below the muscle
  // car and the monster truck while its painted body keeps today's on-screen
  // length and it is still the tallest car), the Formula
  // 4.8 long and low, the bike 2.6 — the bike reads as a bike beside the
  // cars instead of a car-length object. M153: the ball no longer meets one
  // shared box — each ride's `box` in tuning.vehicles is half this length
  // (so the sim nose IS the drawn nose) by its measured body width plus a
  // catch margin; a test pins the two tables together.
  coupe: { id: 'coupe', url: `${base}models/coupe.glb`, length: 3.8, forwardIsZ: false, stockColor: 0xe0202a, rimColor: 0x2a2d33 },  // M154 review: dark gunmetal (the brief's dark wheels)
  // M154 round 3: gloss BLACK rims (the brief's "black multi-spoke wheels";
  // at 0x202226 the lacquered spokes still read charcoal grey in game)
  sports: { id: 'sports', url: `${base}models/sportscar.glb`, length: 4.1, forwardIsZ: false, stockColor: 0x1a4fe8, rimColor: 0x0e0f11 },
  muscle: { id: 'muscle', url: `${base}models/muscle.glb`, length: 4.5, forwardIsZ: false, stockColor: 0x141416, rimColor: 0x74726f },  // M154 round 6: the pack's medium satin silver-grey spokes inside the polished Chrome_rimlip (live's 0x868b94 read light silver, round 3's 0x4a4e57 dark gunmetal); a touch warm so the lab's blue sky does not tint them steel-blue
  pickup: { id: 'pickup', url: `${base}models/pickup.glb`, length: 4.4, forwardIsZ: false, stockColor: 0xe0202a, rimColor: 0x2a2c31 },  // M154: satin black (the pack); 4.4 keeps today's on-screen size with the tucked bumpers
  // M56: the Van became the MONSTER TRUCK. The internal id stays 'van' —
  // it's baked into the online wire protocol (VEHICLE_ROSTER byte codec +
  // ?veh= URLs), and renaming it would force a SIM_VERSION bump for a
  // purely visual change. Only the model, label and menu art changed.
  van: { id: 'van', url: `${base}models/monstertruck.glb`, length: 4.6, forwardIsZ: false, stockColor: 0xe0202a, rimColor: 0x26282c, barrelColor: 0x9e1219 },
  motorcycle: { id: 'motorcycle', url: `${base}models/motorcycle.glb`, length: 2.6, forwardIsZ: false, stockColor: 0xe0202a, rimColor: 0x26282c },
  // M114: the ultimate final unlock (1000 SP, every stat maxed). M151: SOLID
  // red like the pack's single-seater — the M114b chrome→blue gradient
  // (carLook.stockPaintFor) is retired by the owner's brief.
  f1: { id: 'f1', url: `${base}models/f1.glb`, length: 4.8, forwardIsZ: false, stockColor: 0xe0202a, rimColor: 0x26282c },
};

/** The stock livery an undressed `vehicleId` wears on course (M93 — see
 *  VehicleDef.stockColor; unknown ids fall back to the sports red). */
export function stockColorOf(vehicleId: string): number {
  return VEHICLES[vehicleId]?.stockColor ?? VEHICLES.sports.stockColor;
}

/** Display order + labels for the car-selection screen (M35). */
// M62: display order = the coupe starter, then the five earnable cars in
// UNLOCK-COST order (cheapest first — mirrors config VEHICLE_UNLOCKS). Purely
// visual (the wire/codec order is VEHICLE_ROSTER in config/racers.ts).
export const VEHICLE_ORDER: { id: string; label: string }[] = [
  { id: 'coupe', label: 'Coupe' },
  { id: 'pickup', label: 'Pickup' },
  { id: 'muscle', label: 'Muscle' },
  { id: 'sports', label: 'Sports' },
  { id: 'van', label: 'Monster Truck' },
  { id: 'motorcycle', label: 'Motorbike' },
  // M114: label is 'Formula', NOT "F1" — F1 is a protected mark, and the
  // roster labels are plain generic words ('Muscle', 'Sports') by design.
  { id: 'f1', label: 'Formula' },
];

/** The local player's chosen vehicle (offline = grid slot 0). Set from the saved
 *  setting + the car-select screen; defaults to the hero sports car. M51: the
 *  SIM owns each racer's vehicleId now (SimRacer.vehicleId, default roster in
 *  config/racers.ts) — this store is just the local pick that gets fed into
 *  the sim (offline slot 0) and onto online join URLs. Views render straight
 *  from `VEHICLES[r.vehicleId]`. */
let playerVehicleId = 'sports';

export function setPlayerVehicle(id: string): void {
  if (VEHICLES[id]) playerVehicleId = id;
}
export function getPlayerVehicle(): string {
  return playerVehicleId;
}
